import React from 'react';
import { TextField, Box, GlobalStyles, Stack } from '@mui/material';
import { Autocomplete } from '@react-google-maps/api';
import Address from '../../../models/Address';
import BackendService from 'BackendService';
import { StyledHeading } from '../../styles/styles';
import StyledSection from '../../styles/StyledSection';
import AddressListRow from './AddressListRow';

interface LocationManagerProps {
  fundOpportunityId: string;
  addresses: Address[];
  isSubmitting: boolean;
  onAddressesChange: (addresses: Address[]) => void;
  onLocationDetailsChange: (details: { country?: string; state?: string }) => void;
  onLoadingChange: (loading: boolean) => void;
  onError: (error: string) => void;
}

interface LocationManagerState {
  autocomplete?: google.maps.places.Autocomplete;
}

/**
 * LocationManager Component
 *
 * Manages geographic data and address handling for fund opportunities.
 * Provides functionality to add, display, and delete addresses with Google Maps integration.
 */
class LocationManager extends React.Component<LocationManagerProps, LocationManagerState> {
  private addressInputRef = React.createRef<HTMLInputElement>();

  constructor(props: LocationManagerProps) {
    super(props);
    this.state = {};
  }

  private onAddressAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    this.setState({ autocomplete });
  };

  private handleAddressSelect = () => {
    console.log('handleAddressSelect called');
    if (!this.state.autocomplete) {
      console.error('Autocomplete not initialized');
      return;
    }

    const place = this.state.autocomplete.getPlace();
    console.log('Selected place:', place);

    if (!place.place_id) {
      console.error('Place missing place_id');
      return;
    }

    // Process the address components
    const lookupAddressComponents = (type: string) => {
      return place?.address_components?.find((component) => component.types.includes(type))
        ?.long_name;
    };

    const address: Omit<Address, 'id'> = {
      placeId: place.place_id,
      streetAddress:
        lookupAddressComponents('street_address') ||
        `${lookupAddressComponents('street_number')} ${lookupAddressComponents('route')}`,
      fullAddress: place.formatted_address ?? '',
      suburb: lookupAddressComponents('locality') ?? '',
      state: lookupAddressComponents('administrative_area_level_1') ?? '',
      country: lookupAddressComponents('country') ?? '',
      postcode: lookupAddressComponents('postal_code') ?? '',
      latitude: place.geometry?.location?.lat() ?? 0,
      longitude: place.geometry?.location?.lng() ?? 0,
      fundOpportunityId: this.props.fundOpportunityId,
    };

    console.log('Parsed address:', address);

    this.addAddress(address);
  };

  private addAddress = async (address: Omit<Address, 'id'>) => {
    try {
      this.props.onLoadingChange(true);

      await BackendService.createAddress(
        {
          ...address,
          fundOpportunityId: this.props.fundOpportunityId,
        },
        this.props.fundOpportunityId,
      );

      const updatedAddresses = await BackendService.getAddresses(this.props.fundOpportunityId);
      this.props.onAddressesChange(updatedAddresses);
      this.props.onLocationDetailsChange({ country: address.country, state: address.state });

      // The Autocomplete input isn't a controlled field (Google's widget writes
      // directly into the DOM node), so it keeps showing the just-added address
      // and can be left in a stuck, unfocusable state after Chrome's own
      // autofill heuristics kick in. Reset it explicitly once the address has
      // been saved.
      if (this.addressInputRef.current) {
        this.addressInputRef.current.value = '';
        this.addressInputRef.current.blur();
      }
    } catch (error) {
      console.error('Error adding address:', error);
      this.props.onError('Failed to add address. Please try again.');
    } finally {
      this.props.onLoadingChange(false);
    }
  };

  private deleteAddress = async (address: Address) => {
    if (!address.id) {
      console.error('Cannot delete address without ID');
      return;
    }

    try {
      this.props.onLoadingChange(true);

      await BackendService.deleteAddress(address.id);

      // Update the addresses list by removing the deleted address
      const updatedAddresses = this.props.addresses.filter((addr) => addr.id !== address.id);
      this.props.onAddressesChange(updatedAddresses);
    } catch (error) {
      console.error('Error deleting address:', error);
      this.props.onError('Failed to delete address. Please try again.');
    } finally {
      this.props.onLoadingChange(false);
    }
  };

  render() {
    const { addresses, isSubmitting } = this.props;

    return (
      <StyledSection tone="tone1">
        <StyledHeading>Addresses</StyledHeading>

        {/* Google's pac-container dropdown is appended to document.body outside
            this component, so it needs its own z-index to avoid sitting behind
            (and appearing to block clicks on) other positioned elements. It
            also stays in the DOM with its box-shadow visible after the user
            clicks away without picking a suggestion — hide it whenever it has
            no suggestion items left. */}
        <GlobalStyles
          styles={{
            '.pac-container': { zIndex: 1500 },
            '.pac-container:empty': { display: 'none' },
          }}
        />

        <StyledHeading headerVariant="subheading">Add Address</StyledHeading>
        <Autocomplete
          options={{ componentRestrictions: { country: 'au' } }}
          onLoad={this.onAddressAutocompleteLoad}
          onPlaceChanged={this.handleAddressSelect}
        >
          <TextField
            variant="outlined"
            placeholder="Enter an address"
            disabled={isSubmitting}
            size="small"
            fullWidth
            autoComplete="off"
            inputRef={this.addressInputRef}
          />
        </Autocomplete>

        {addresses.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack spacing={1.5}>
              {addresses.map((address) => (
                <AddressListRow
                  key={address.id ?? address.placeId}
                  address={address}
                  onRemove={() => this.deleteAddress(address)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </StyledSection>
    );
  }
}

export default LocationManager;
